// A record's actions open from one quiet mark, the kebab, at the top right
// of the pane or the row they act on. The same mark everywhere, so it is
// learned once.

import { ActionIcon, Menu, type ActionIconProps, type MenuItemProps } from '@mantine/core';
import { IconDotsVertical, type Icon } from '@tabler/icons-react';
import type { ComponentPropsWithoutRef, ComponentPropsWithRef } from 'react';

import { ICON, ICON_INLINE } from '../theme';

/**
 * The mark a menu opens from. Spreads what `Menu.Target` hands it. While one
 * of its actions is on its way (`loading`) it stands down rather than
 * spinning: the bar along the page's or the dialog's edge says it is working
 * (components/top-load-bar.tsx).
 */
export function ActionsTrigger({ label, loading, disabled, ...others }: { label: string } & ActionIconProps & Omit<ComponentPropsWithRef<'button'>, keyof ActionIconProps>) {
  return (
    <ActionIcon variant="subtle" color="gray" size={36} aria-label={label} style={{ flex: 'none' }} {...others} disabled={!!(disabled || loading)} {...(loading ? { 'aria-busy': true } : {})}>
      <IconDotsVertical {...ICON} />
    </ActionIcon>
  );
}

/**
 * One line of a record's menu: an icon in the secondary ink, then what it
 * does in two or three words. `danger` is for what cannot be taken back, and
 * colors the icon with the words. A line that does not apply to the record
 * is left out rather than shown disabled with a reason.
 */
export function MenuAction({ icon: Glyph, danger = false, children, ...others }: { icon: Icon; danger?: boolean } & MenuItemProps & Omit<ComponentPropsWithoutRef<'button'>, keyof MenuItemProps>) {
  return (
    <Menu.Item leftSection={<Glyph {...ICON_INLINE} {...(danger ? {} : { color: 'var(--mantine-color-dimmed)' })} />} {...(danger ? { color: 'danger' } : {})} {...others}>
      {children}
    </Menu.Item>
  );
}
